import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, Trash2, FileText, Image as ImageIcon, File as FileIcon, Plus, Filter } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "bail", label: "Bail" },
  { id: "etat_lieux", label: "État des lieux" },
  { id: "assurance", label: "Assurance" },
  { id: "identite", label: "Pièce d'identité" },
  { id: "caution", label: "Acte de caution" },
  { id: "quittance", label: "Quittance" },
  { id: "courrier", label: "Courrier" },
  { id: "autre", label: "Autre" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map(c => [c.id, c.label]));

function FileIconFor({ ct }) {
  if (ct?.startsWith("image/")) return <ImageIcon className="w-4 h-4 text-zinc-600" />;
  if (ct === "application/pdf") return <FileText className="w-4 h-4 text-red-600" />;
  return <FileIcon className="w-4 h-4 text-zinc-600" />;
}

const fmtSize = (b) => {
  if (b < 1024) return `${b} o`;
  if (b < 1024*1024) return `${(b/1024).toFixed(1)} Ko`;
  return `${(b/1024/1024).toFixed(2)} Mo`;
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [props, setProps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [filterEntity, setFilterEntity] = useState("all");
  const fileInput = useRef(null);
  const [form, setForm] = useState({ category: "autre", entity_type: "general", entity_id: "", title: "" });

  const load = () => Promise.all([
    api.get("/documents"),
    api.get("/properties"),
    api.get("/tenants"),
    api.get("/leases"),
  ]).then(([d, p, t, l]) => { setDocs(d.data); setProps(p.data); setTenants(t.data); setLeases(l.data); });

  useEffect(() => { load(); }, []);

  const openUpload = () => { setForm({ category: "autre", entity_type: "general", entity_id: "", title: "" }); setOpen(true); if (fileInput.current) fileInput.current.value = ""; };

  const submit = async () => {
    const f = fileInput.current?.files?.[0];
    if (!f) { toast.error("Sélectionnez un fichier"); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error("Fichier > 25 Mo"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", form.category);
      fd.append("entity_type", form.entity_type);
      fd.append("entity_id", form.entity_id || "");
      fd.append("title", form.title || "");
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document téléversé");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };

  const downloadDoc = async (d) => {
    try {
      const token = localStorage.getItem("gl_token");
      const r = await fetch(`${API}/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Erreur téléchargement");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const del = async (d) => {
    if (!confirm(`Supprimer "${d.title}" ?`)) return;
    await api.delete(`/documents/${d.id}`); toast.success("Supprimé"); load();
  };

  const entityLabel = (d) => {
    if (d.entity_type === "property") { const p = props.find(x => x.id === d.entity_id); return p ? p.address : "Bien"; }
    if (d.entity_type === "tenant") { const t = tenants.find(x => x.id === d.entity_id); return t ? `${t.first_name} ${t.last_name}` : "Locataire"; }
    if (d.entity_type === "lease") return "Bail";
    return "Général";
  };

  const filtered = docs.filter(d => {
    if (filterCat !== "all" && d.category !== filterCat) return false;
    if (filterEntity !== "all" && d.entity_type !== filterEntity) return false;
    return true;
  });

  // Grouped counts
  const counts = CATEGORIES.map(c => ({ ...c, count: docs.filter(d => d.category === c.id).length }));

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Archives</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:'Outfit'}}>Documents</h1>
          <p className="text-sm text-zinc-500 mt-1">{docs.length} document(s) · classement par bien, locataire ou catégorie</p>
        </div>
        <Button data-testid="upload-doc-btn" onClick={openUpload}><Upload className="w-4 h-4 mr-1.5" />Téléverser un document</Button>
      </div>

      {/* Category overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {counts.map(c => (
          <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => setFilterCat(filterCat === c.id ? "all" : c.id)}
                  className={`card-flat p-3 text-left transition-colors ${filterCat === c.id ? "bg-zinc-900 text-white border-zinc-900" : ""}`}>
            <div className="text-overline" style={{color: filterCat === c.id ? "rgba(255,255,255,.6)" : undefined}}>{c.label}</div>
            <div className={`text-2xl font-bold mt-1 ${filterCat === c.id ? "text-white" : ""}`} style={{fontFamily:'Outfit'}}>{c.count}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card className="card-flat p-4 flex flex-wrap gap-3 items-end">
        <div className="flex items-center gap-1.5 text-zinc-600 text-sm"><Filter className="w-4 h-4" /> Filtrer :</div>
        <div>
          <Label className="text-xs">Catégorie</Label>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={filterEntity} onValueChange={setFilterEntity}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="property">Bien</SelectItem>
              <SelectItem value="tenant">Locataire</SelectItem>
              <SelectItem value="lease">Bail</SelectItem>
              <SelectItem value="general">Général</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* List */}
      <Card className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">Document</th><th className="px-4 py-3">Catégorie</th>
              <th className="px-4 py-3">Rattaché à</th><th className="px-4 py-3">Taille</th>
              <th className="px-4 py-3">Date</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`doc-row-${d.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileIconFor ct={d.content_type} />
                    <div>
                      <div className="font-medium">{d.title}</div>
                      <div className="text-xs text-zinc-500">{d.original_filename}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"><Badge variant="outline">{CATEGORY_LABEL[d.category]}</Badge></td>
                <td className="px-4 py-3 text-xs">{entityLabel(d)}</td>
                <td className="px-4 py-3 mono text-xs">{fmtSize(d.size)}</td>
                <td className="px-4 py-3 text-xs">{fmtDate(d.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`dl-doc-${d.id}`}><Download className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(d)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500"><FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucun document. Cliquez sur "Téléverser un document".</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Téléverser un document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fichier (PDF, image, doc — max 25 Mo) *</Label>
              <Input data-testid="doc-file-input" ref={fileInput} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt" />
            </div>
            <div><Label>Titre (optionnel)</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Ex: Bail M. Dupont — Mars 2026" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rattacher à</Label>
                <Select value={form.entity_type} onValueChange={(v) => setForm({...form, entity_type: v, entity_id: ""})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Général</SelectItem>
                    <SelectItem value="property">Un bien</SelectItem>
                    <SelectItem value="tenant">Un locataire</SelectItem>
                    <SelectItem value="lease">Un bail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.entity_type === "property" && (
              <div>
                <Label>Bien</Label>
                <Select value={form.entity_id} onValueChange={(v) => setForm({...form, entity_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>{props.map(p => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.entity_type === "tenant" && (
              <div>
                <Label>Locataire</Label>
                <Select value={form.entity_id} onValueChange={(v) => setForm({...form, entity_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>{tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.entity_type === "lease" && (
              <div>
                <Label>Bail</Label>
                <Select value={form.entity_id} onValueChange={(v) => setForm({...form, entity_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>{leases.map(l => { const p = props.find(x => x.id === l.property_id); return <SelectItem key={l.id} value={l.id}>{p?.address || l.id} · {fmtDate(l.start_date)}</SelectItem>; })}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={uploading} data-testid="doc-submit-btn">
              {uploading ? "Envoi…" : <><Upload className="w-4 h-4 mr-1.5" />Téléverser</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
